import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { setTheme } from '../features/uiSlice';
import { setCredentials } from '../features/authSlice';
import { authAPI } from '../services/authApi';
import store from '../store/store';
import styles from '../css/Settings.module.css';

const SECTIONS = [
  { id: 'appearance', label: 'Внешний вид', icon: (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 3a6.364 6.364 0 0 0 9 9 9 9 0 1 1-9-9Z"/>
    </svg>
  )},
  { id: 'notifications', label: 'Уведомления', icon: (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 0 1-3.46 0"/>
    </svg>
  )},
  { id: 'security', label: 'Безопасность и вход', icon: (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
      <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
    </svg>
  )},
];

const PROVIDERS = [
  {
    id: 'google',
    label: 'Google',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
      </svg>
    ),
  },
  {
    id: 'telegram',
    label: 'Telegram',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#229ED9">
        <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.894 8.221l-1.97 9.28c-.145.658-.537.818-1.084.508l-3-2.21-1.446 1.394c-.14.18-.357.295-.6.295l.213-3.054 5.56-5.022c.24-.213-.054-.334-.373-.121l-6.869 4.326-2.96-.924c-.64-.203-.658-.64.135-.954l11.566-4.458c.538-.196 1.006.128.832.941z"/>
      </svg>
    ),
  },
];

const Settings = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const theme = useSelector((state) => state.ui.theme);
  const user = useSelector((state) => state.auth.user);
  const accessToken = useSelector((state) => state.auth.accessToken);
  const [activeSection, setActiveSection] = useState('appearance');
  const [newEpisodes, setNewEpisodes] = useState(true);
  const [recommendations, setRecommendations] = useState(true);
  const [emailNotifications, setEmailNotifications] = useState(false);
  const [linkedProviders, setLinkedProviders] = useState(new Set(
    user?.providers ?? user?.identities?.map((i) => i.provider) ?? []
  ));

  const fetchProviders = useCallback(() => {
    if (!accessToken) return;
    authAPI.getMe(accessToken)
      .then((res) => {
        const u = res.data?.data;
        setLinkedProviders(new Set(u?.providers ?? []));
      })
      .catch(() => {});
  }, [accessToken]);

  useEffect(() => {
    fetchProviders();
  }, [fetchProviders]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('connected')) {
      setActiveSection('security');
      fetchProviders();
      navigate('/settings', { replace: true });
    }
  }, [location.search, fetchProviders, navigate]);

  const handleConnectGoogle = async () => {
    let token = accessToken;
    try {
      const res = await authAPI.refresh();
      const newToken = res.data?.data?.access_token;
      if (newToken) {
        store.dispatch(setCredentials({ user, accessToken: newToken }));
        token = newToken;
      }
    } catch {}
    window.location.href = authAPI.getGoogleConnectUrl(token);
  };

  return (
    <div className={styles.settingsPage}>
      <div className={styles.container}>
        <button className={styles.backButton} onClick={() => navigate(-1)}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
          Назад
        </button>

        <h1 className={styles.title}>Настройки</h1>
        <p className={styles.subtitle}>Персонализируйте свой опыт</p>

        <div className={styles.grid}>
          {/* Левая плитка — навигация */}
          <div className={styles.tile}>
            <p className={styles.tileLabel}>Разделы</p>
            <nav className={styles.nav}>
              {SECTIONS.map((s) => (
                <button
                  key={s.id}
                  className={`${styles.navItem} ${activeSection === s.id ? styles.navItemActive : ''}`}
                  onClick={() => setActiveSection(s.id)}
                >
                  <span className={styles.navIcon}>{s.icon}</span>
                  {s.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Правая плитка — содержимое */}
          <div className={styles.tile}>
            {activeSection === 'appearance' && (
              <>
                <p className={styles.tileLabel}>Внешний вид</p>
                <div className={styles.themeSelector}>
                  <div className={styles.themeInfo}>
                    <span className={styles.themeLabel}>Тема</span>
                    <span className={styles.themeDescription}>Светлая или тёмная тема</span>
                  </div>
                  <div className={styles.themeButtons}>
                    <button
                      className={`${styles.themeButton} ${theme === 'light' ? styles.activeTheme : ''}`}
                      onClick={() => dispatch(setTheme('light'))}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                        <circle cx="12" cy="12" r="5"/>
                        <path d="M12 1v2m0 18v2M4.22 4.22l1.42 1.42m12.72 12.72l1.42 1.42M1 12h2m18 0h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/>
                      </svg>
                    </button>
                    <button
                      className={`${styles.themeButton} ${theme === 'dark' ? styles.activeTheme : ''}`}
                      onClick={() => dispatch(setTheme('dark'))}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
                      </svg>
                    </button>
                  </div>
                </div>
              </>
            )}

            {activeSection === 'notifications' && (
              <>
                <p className={styles.tileLabel}>Уведомления</p>
                <div className={styles.settingItem}>
                  <div className={styles.settingInfo}>
                    <span className={styles.settingLabel}>Новые серии</span>
                    <span className={styles.settingDescription}>Уведомление о выходе новых серий</span>
                  </div>
                  <label className={styles.toggle}>
                    <input type="checkbox" checked={newEpisodes} onChange={(e) => setNewEpisodes(e.target.checked)} />
                    <span className={styles.toggleSlider}></span>
                  </label>
                </div>
                <div className={styles.settingItem}>
                  <div className={styles.settingInfo}>
                    <span className={styles.settingLabel}>Рекомендации</span>
                    <span className={styles.settingDescription}>Персональные рекомендации дорам</span>
                  </div>
                  <label className={styles.toggle}>
                    <input type="checkbox" checked={recommendations} onChange={(e) => setRecommendations(e.target.checked)} />
                    <span className={styles.toggleSlider}></span>
                  </label>
                </div>
                <div className={styles.settingItem}>
                  <div className={styles.settingInfo}>
                    <span className={styles.settingLabel}>Email рассылка</span>
                    <span className={styles.settingDescription}>Получать новости на почту</span>
                  </div>
                  <label className={styles.toggle}>
                    <input type="checkbox" checked={emailNotifications} onChange={(e) => setEmailNotifications(e.target.checked)} />
                    <span className={styles.toggleSlider}></span>
                  </label>
                </div>
              </>
            )}

            {activeSection === 'security' && (
              <>
                <p className={styles.tileLabel}>Связанные аккаунты</p>
                <div className={styles.providerList}>
                  {PROVIDERS.map((provider) => {
                    const linked = linkedProviders.has(provider.id);
                    return (
                      <div key={provider.id} className={styles.providerItem}>
                        <div className={styles.providerInfo}>
                          <span className={styles.providerIcon}>{provider.icon}</span>
                          <span className={styles.providerLabel}>{provider.label}</span>
                        </div>
                        {linked ? (
                          <span className={styles.providerLinkedBadge}>
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                              <polyline points="20 6 9 17 4 12"/>
                            </svg>
                            Привязан
                          </span>
                        ) : (
                          <button
                            className={styles.providerConnect}
                            onClick={provider.id === 'google' ? handleConnectGoogle : undefined}
                            disabled={provider.id !== 'google'}
                          >
                            Привязать
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
